/**
 * AdminProductsListPage - Admin products list page
 * [PHASE 3] Advanced E-commerce Features
 *
 * Features:
 * - Display all products in a table
 * - Search by name or SKU
 * - Pagination
 * - Batch operations (publish/unpublish/delete)
 * - Navigate to edit page
 * - Delete products with confirmation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DesktopLayout from '../../../components/layout/DesktopLayout';
import ProductsTable from '../../../components/admin/products/ProductsTable';
import { batchProducts } from '../../../services/productsApi';
import type { ProductListItem } from '../../../types';
import './AdminProductsListPage.css';

const AdminProductsListPage = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleEdit = (product: ProductListItem) => {
    navigate(`/admin/products/${product.id}/edit`);
  };

  const handleBatchAction = async (action: 'publish' | 'unpublish' | 'delete', productIds: string[]) => {
    if (productIds.length === 0) {
      setToast({ type: 'error', message: '請先選擇商品' });
      return;
    }

    const confirmMessages = {
      publish: `確定要發布 ${productIds.length} 個商品嗎？`,
      unpublish: `確定要取消發布 ${productIds.length} 個商品嗎？`,
      delete: `確定要刪除 ${productIds.length} 個商品嗎？此操作無法復原。`,
    };

    if (!confirm(confirmMessages[action])) {
      return;
    }

    try {
      const response = await batchProducts({ action, productIds });

      if (response.success) {
        const { processed, failed, errors } = response.data;
        if (failed > 0) {
          setToast({
            type: 'error',
            message: `成功: ${processed}, 失敗: ${failed}. ${errors.map(e => e.error).join(', ')}`,
          });
        } else {
          setToast({
            type: 'success',
            message: `已${action === 'delete' ? '刪除' : action === 'publish' ? '發布' : '取消發布'} ${processed} 個商品`,
          });
        }
        // Refresh will be handled by ProductsTable component
        window.location.reload();
      } else {
        setToast({ type: 'error', message: '操作失敗，請稍後再試' });
      }
    } catch (error) {
      console.error('Batch action failed:', error);
      setToast({ type: 'error', message: '操作失敗，請稍後再試' });
    }
  };

  const handleAddProduct = () => {
    navigate('/admin/products/new');
  };

  const handleCloseToast = () => {
    setToast(null);
  };

  return (
    <DesktopLayout>
      <div className="admin-products-list-page">
        {/* Header */}
        <header className="admin-products-list-page__header">
          <div className="admin-products-list-page__title-section">
            <h1 className="admin-products-list-page__title">商品管理</h1>
            <p className="admin-products-list-page__subtitle">管理所有商品資訊</p>
          </div>
          <button
            className="admin-products-list-page__add-btn"
            onClick={handleAddProduct}
            type="button"
          >
            <span className="admin-products-list-page__add-icon">+</span>
            新增商品
          </button>
        </header>

        {/* Toast Notification */}
        {toast && (
          <div className={`admin-products-list-page__toast admin-products-list-page__toast--${toast.type}`}>
            <span className="admin-products-list-page__toast-message">{toast.message}</span>
            <button
              className="admin-products-list-page__toast-close"
              onClick={handleCloseToast}
              type="button"
            >
              ×
            </button>
          </div>
        )}

        {/* Products Table */}
        <div className="admin-products-list-page__table-container">
          <ProductsTable onEdit={handleEdit} onBatchAction={handleBatchAction} />
        </div>
      </div>
    </DesktopLayout>
  );
};

export default AdminProductsListPage;
